import type { DomainSubdomain } from '@/api/dashboard';

/**
 * Chart sections hidden from the Lens "Chart Section Trends" surfaces
 * (banner call-out and the drill-down modal).
 *
 * V&V K4 (PLA-1356): Vital Signs indicators are reported, but Vitals is not a
 * chart *section* the way HPI / Physical Exam / MDM are, so it is excluded
 * from the section trend views for now. The data still flows from the API and
 * still counts toward the composite score; this is display-only.
 *
 * Matching is by normalized id or name (non-alphanumerics collapsed) so it
 * holds whether the API sends `id: "vital-signs"` / `name: "Vital Signs"`
 * (what /domains/rsq/subdomain-trend emits today) or a "vitals" variant.
 */
const HIDDEN_SECTION_KEYS = new Set(['vitals', 'vitalsigns', 'vitalsign']);

const norm = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]+/g, '');

export function isHiddenChartSection(sub: Pick<DomainSubdomain, 'id' | 'name'>): boolean {
  return HIDDEN_SECTION_KEYS.has(norm(sub.id)) || HIDDEN_SECTION_KEYS.has(norm(sub.name));
}

/** The sub-domains to show on the Chart Section Trends surfaces. */
export function visibleChartSections<T extends Pick<DomainSubdomain, 'id' | 'name'>>(
  subs: readonly T[],
): T[] {
  return subs.filter((s) => !isHiddenChartSection(s));
}
